import { FactoryStaticOpts } from 'imask';
import {
    Control,
    Controller,
    ControllerProps,
    FieldError,
    FieldValues,
    Path,
} from 'react-hook-form';
import { TextField, TextFieldProps } from '@mui/material';

import { MaskedTextField } from './MaskedTextField';

export interface MaskedTextFieldElementProps<
    T extends FieldValues = FieldValues,
> extends Omit<TextFieldProps, 'name'> {
    inputProps?: FactoryStaticOpts;
    validation?: ControllerProps['rules'];
    name: Path<T>;
    parseError?: (error: FieldError) => string;
    control?: Control<FieldValues>;
}

export function MaskedTextFieldElement<
    TFieldValues extends FieldValues = FieldValues,
>({
    validation = {},
    parseError,
    type,
    required,
    name,
    control,
    inputProps,
    slotProps,
    ...rest
}: MaskedTextFieldElementProps<TFieldValues>): React.ReactNode {
    if (required && !validation.required) {
        validation.required = 'This field is required';
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={validation}
            render={({
                field: { value, onChange, onBlur, ref },
                fieldState: { error },
            }) => (
                <TextField
                    {...rest}
                    name={name}
                    value={value ?? ''}
                    onChange={ev => {
                        onChange(
                            type === 'number'
                                ? Number(ev.target.value)
                                : ev.target.value
                        );
                        if (typeof rest.onChange === 'function') {
                            rest.onChange(ev);
                        }
                    }}
                    onBlur={onBlur}
                    required={required}
                    type={type}
                    error={!!error}
                    helperText={
                        error
                            ? typeof parseError === 'function'
                                ? parseError(error)
                                : error.message
                            : rest.helperText
                    }
                    inputRef={ref}
                    slotProps={{
                        ...slotProps,
                        input: {
                            ...slotProps?.input,
                            inputComponent: MaskedTextField,
                            inputProps: {
                                ...inputProps,
                            },
                        },
                    }}
                />
            )}
        />
    );
}
